import type { DataState, GlanceState } from './states.js';

/**
 * ============================================================================
 * H3 — TILE RENDER CONTRACT  (ADR-001 §8 H3; ADR-002 §5; glance-grammar-spec)
 * ============================================================================
 *
 * A tile is `config → data → glance view-model`. This module defines the
 * view-model that DECOUPLES layout from tile internals:
 *
 *   - tile-dev PRODUCES a `GlanceViewModel` (via the adapter's `glanceView`, H2),
 *   - platform-dev RENDERS a `GlanceViewModel` into a grid cell,
 *
 * neither importing the other's concrete code. platform-dev's renderer can lay
 * out ANY tile knowing only this shape (ADR-001 H3).
 *
 * The grammar is fixed (glance-grammar-spec §1.1; PRODUCT.md): EXACTLY one primary
 * fact, one delta/trend, one state color — no tile may introduce a second focal
 * point. The character caps below are the spec's (§1.1, amended by D-018):
 * primary ≤ 80, delta ≤ 20, label ≤ 16. They are SAFETY bounds, not display
 * budgets — visual fit is the renderer's single-line CSS ellipsis. Numeric
 * primaries ("72°", "$739") stay ≤ 8 by construction; text primaries (headline,
 * event title, note preview) carry a whole glanceable phrase. The original
 * primary ≤ 8 contract truncated live headlines to "South Af" on launch day —
 * a number's budget applied to prose (D-018).
 *
 * SECURITY: every string here is PLAIN TEXT and rendered as text, never HTML
 * (ADR-002 §7 #2). No field may carry markup.
 * ============================================================================
 */

/** glance-grammar-spec §1.1 max lengths (D-018) — the renderer assumes these hold. */
export const GLANCE_LIMITS = {
  PRIMARY_MAX_CHARS: 80,
  DELTA_MAX_CHARS: 20,
  LABEL_MAX_CHARS: 16,
} as const;

/**
 * Direction glyph accompanying the delta (glance-grammar-spec §4.3): state is
 * NEVER conveyed by color alone, so the delta always carries a direction symbol.
 */
export const DIRECTION_GLYPHS = {
  up: '↑',
  down: '↓',
  flat: '→',
  dot: '●',
} as const;
export type DirectionGlyph = keyof typeof DIRECTION_GLYPHS;

/**
 * The three content slots (glance-grammar-spec §1.1). Values are the ALREADY
 * FORMATTED display strings the adapter produced from normalized data — the
 * renderer does no formatting, only placement + styling.
 */
export interface GlanceSlots {
  /** The one big fact, ≤ 80 chars (safety bound): numeric facts ("72°", "3 – 1",
   *  "$182.40") self-bound far below; text facts (headline, event title) carry a
   *  phrase and the renderer ellipsizes to fit (D-018). Required. */
  readonly primary: string;
  /** The trend line, ≤ 20 chars: "↑4° vs yesterday", "▲ 2.3%", "in 3h". Nullable. */
  readonly delta: string | null;
  /** Optional direction glyph paired with the delta (§4.3). */
  readonly direction?: DirectionGlyph;
}

/**
 * The glance view-model — the H3 hand-off object. `config → data →` THIS.
 *
 * `dataState` (freshness) and `glanceState` (semantic color) are the two axes
 * from states.ts; the renderer maps `glanceState` → accent/tint (glance-spec
 * §4.1) and `dataState` → freshness badge / loading & error chrome (§6).
 */
export interface GlanceViewModel {
  /** Fetch/freshness lifecycle (drives loading/error/stale chrome, §6). */
  readonly dataState: DataState;
  /** Semantic color of the primary fact (drives tint + accent, §4.1). */
  readonly glanceState: GlanceState;
  /** The three content slots (§1.1). */
  readonly slots: GlanceSlots;
  /** Dimmed tile label, ≤ 16 chars, plain text (e.g. "SF Weather"). Nullable. */
  readonly label: string | null;
  /**
   * Epoch ms the underlying data was fetched, or null when never fetched
   * (loading with no prior value). The renderer formats this relatively
   * ("just now" / "4m ago" / "stale") — glance-spec §1.1 timestamp slot.
   */
  readonly fetchedAt: number | null;
  /**
   * Optional epoch-ms retry hint, carried through from a rate-limited envelope
   * (H1 `retryAtMs`) so the renderer can show "updating soon" without dropping
   * the signal at the render boundary. Absent unless `dataState === 'rateLimited'`.
   */
  readonly retryAtMs?: number;
}

/**
 * ============================================================================
 * CENTRAL GLANCE_LIMITS CLAMP  (code-review MEDIUM, 2026-07-24)
 * ============================================================================
 * The renderer RELIES on the caps above holding (a < 1s glance in a tiny cell).
 * tile-dev's adapters are *supposed* to respect them, but the render contract
 * must GUARANTEE it regardless of adapter discipline — a single sloppy adapter
 * must not be able to break a tile's layout. So the shared H1→H3 bridge and the
 * state-view factories both run every string through this clamp before it reaches
 * the renderer.
 *
 * TRUNCATION RULE (documented, deliberate): clamp by `String.prototype.slice` to
 * the cap length — NO ellipsis / "…" injection. Rationale: the caps (80/20/16)
 * are safety bounds, not display widths; injected ellipses would double up with
 * the renderer's own CSS `text-overflow: ellipsis`, which is the single honest
 * place visual truncation happens (D-018). The CONTRACT guarantees only that
 * the string is never longer than the cap. Byte-for-byte: an already-conforming
 * string is returned unchanged (same reference-equal characters, just fewer of
 * them when over-limit). Plain text only — never HTML (ADR-002 §7 #2).
 * ============================================================================
 */

/** Slice a string to `max` chars — deterministic, no ellipsis. Under-limit ⇒ unchanged. */
function clampSlot(value: string, max: number): string {
  return value.length <= max ? value : value.slice(0, max);
}

/** Same as `clampSlot` but null-transparent (a null slot stays null, never ""). */
function clampNullable(value: string | null, max: number): string | null {
  return value === null ? null : clampSlot(value, max);
}

/**
 * Enforce GLANCE_LIMITS on a view-model. Pure & total: returns a NEW view-model
 * whose `primary`/`delta`/`label` are guaranteed within the caps; every other
 * field (states, direction, fetchedAt, optional retryAtMs) is carried through
 * untouched. Never mutates the input. Idempotent (clamping twice == once).
 */
export function clampGlanceViewModel(vm: GlanceViewModel): GlanceViewModel {
  return {
    ...vm,
    slots: {
      ...vm.slots,
      primary: clampSlot(vm.slots.primary, GLANCE_LIMITS.PRIMARY_MAX_CHARS),
      delta: clampNullable(vm.slots.delta, GLANCE_LIMITS.DELTA_MAX_CHARS),
    },
    label: clampNullable(vm.label, GLANCE_LIMITS.LABEL_MAX_CHARS),
  };
}

/**
 * State-view factories (glance-grammar-spec §6). These are the SHARED way to
 * turn a non-data envelope into a view-model, so platform-dev and tile-dev render
 * loading/error/stale/rate-limited identically instead of each hand-rolling the
 * mapping (the exact divergence H3 exists to prevent). The ok/stale-WITH-data
 * path goes through the adapter's `glanceView`; these cover the rest.
 *
 * Each factory runs its (caller-supplied) `label` through the clamp so even a
 * hand-passed over-limit label can never reach the renderer over-length.
 */

/**
 * Loading view — first paint, before any data (§6.1). Placement of the em-dash
 * placeholder is the renderer's job; this carries the state.
 */
export function loadingView(label: string | null = null): GlanceViewModel {
  return {
    dataState: 'loading',
    glanceState: 'neutral',
    slots: { primary: '', delta: null },
    label: clampNullable(label, GLANCE_LIMITS.LABEL_MAX_CHARS),
    fetchedAt: null,
  };
}

/**
 * Error view (§6.2). Grey accent (`glanceState: 'stale'` → grey, we don't know
 * the real state). Carries `fetchedAt` of the last-known value if any, so the
 * renderer can show a faded last value + "Unavailable".
 */
export function errorView(
  label: string | null = null,
  lastFetchedAt: number | null = null,
): GlanceViewModel {
  return {
    dataState: 'error',
    glanceState: 'stale',
    slots: { primary: '', delta: null },
    label: clampNullable(label, GLANCE_LIMITS.LABEL_MAX_CHARS),
    fetchedAt: lastFetchedAt,
  };
}

/**
 * Rate-limited view — a soft error with a retry hint (§6.2 variant). Grey accent;
 * the renderer shows "updating soon" using `retryAtMs`.
 */
export function rateLimitedView(
  label: string | null = null,
  retryAtMs?: number,
  lastFetchedAt: number | null = null,
): GlanceViewModel {
  return {
    dataState: 'rateLimited',
    glanceState: 'stale',
    slots: { primary: '', delta: null },
    label: clampNullable(label, GLANCE_LIMITS.LABEL_MAX_CHARS),
    fetchedAt: lastFetchedAt,
    ...(retryAtMs !== undefined ? { retryAtMs } : {}),
  };
}
